import * as React from 'react';
import { useDispatch } from 'react-redux';

import { alertError, alertSuccess } from '../../../../store/reducers/Alert';
import { CommentButton } from '../../../../components/buttons/CommentButton';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { ContractorProjectDocActions } from '../../../../helpers/constants/modelActions/ContractorProjectDocActions';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { ValidationResult } from '../../../../types/models/error/ValidationResult';
import { WorkflowInstance } from '../../../workflows/models/WorkflowInstance';
import { DictIntEntity } from '../../../../types/models/base/DictIntEntity';
import { DocTemplateFolderTypeVals } from '../../../../helpers/constants/modelValues/DocTemplateFolderTypeVals';
import { User } from '../../../settings/users/models/User';
import { WorkflowsVals } from '../../../../helpers/constants/modelValues/WorkflowVals';
import { NewMultiSelect } from '../../../../components/synced/ui/NewMultiSelect/NewMultiSelect';
import { Dialog } from 'primereact/dialog';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';
import { NewCheckbox } from '../../../../components/synced/ui/NewCheckbox/NewCheckbox';
import { PaperSigningStatusVals } from '../../../../helpers/constants/modelValues/PaperSigningStatusVals';
import { ProjectDocFolderTreeNode } from '../../models/ProjectDocFolderTreeNode';
import { ProjectDocFolderSubTypes } from '../../../../helpers/mapping/ProjectDocFolderSubTypes';

interface RegistryFolderOption {
    Id: number;
    Title: string;
    Cypher: string;
    Label: string;
}

const registryFolderTypeIds = new Set<number>([
    DocTemplateFolderTypeVals.Tom,
    DocTemplateFolderTypeVals.Act,
    DocTemplateFolderTypeVals.ActDocument,
]);

const getRegistryFolderOptions = (treeNodes: ProjectDocFolderTreeNode[]): RegistryFolderOption[] => {
    const options: RegistryFolderOption[] = [];
    const addedIds = new Set<number>();
    const nodes = [...treeNodes].reverse();

    while (nodes.length > 0) {
        const node = nodes.pop()!;
        const children = node.children ?? [];

        for (let index = children.length - 1; index >= 0; index--) {
            nodes.push(children[index]);
        }

        const id = typeof node.key === 'number' ? node.key : Number(node.key);
        const folderTypeId = node.FolderTypeId ?? node.data?.FolderTypeId;
        if (
            !Number.isSafeInteger(id) ||
            id <= 0 ||
            addedIds.has(id) ||
            node.data?.IsDeleted === true ||
            !registryFolderTypeIds.has(folderTypeId ?? 0)
        ) {
            continue;
        }

        const cypher = (node.Cypher ?? node.data?.Cypher ?? '').trim();
        const title = node.data?.Title ?? (typeof node.label === 'string' ? node.label : '');
        const orderNumber = Number(node['OrderNumber']);
        const changeMarker =
            (node.SubTypeId ?? node.data?.SubTypeId) === ProjectDocFolderSubTypes.Change
                ? Number.isSafeInteger(orderNumber) && orderNumber > 0
                    ? ` изм-${orderNumber}`
                    : ' изм'
                : '';

        addedIds.add(id);
        options.push({
            Id: id,
            Title: title,
            Cypher: cypher,
            Label: `${cypher ? `[${cypher}]` : '[шифра нет]'}${changeMarker} - ${title}`,
        });
    }

    return options;
};

interface KAProjectDocFolderActionsProps {
    projectDocId: number;
    treeNodes: ProjectDocFolderTreeNode[];
    onProjectDocStatusUpdated: () => void;
    actions: ModelAction[];
    loadActions: () => void;
    onPaperSigningGenerated?: () => void;
}

export const KAProjectDocFolderActions: React.FC<KAProjectDocFolderActionsProps> = ({
    projectDocId,
    treeNodes,
    onProjectDocStatusUpdated,
    actions,
    loadActions,
    onPaperSigningGenerated
}) => {
    const dispatch = useDispatch();

    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();

    const [visible, setVisible] = React.useState(false);

    const cyphers = React.useMemo(() => getRegistryFolderOptions(treeNodes), [treeNodes]);
    const [selectedCyphers, setSelectedCyphers] = React.useState<RegistryFolderOption[]>([]);

    const [selectedStatuses, setSelectedStatuses] = React.useState<DictIntEntity[]>([]);
    const statuses: DictIntEntity[] = React.useMemo(
        () => [
            { Id: 1, Title: 'На проверке' },
            { Id: 2, Title: 'В работе у КА' },
            { Id: 3, Title: 'Снято' },
            { Id: 4, Title: 'Гарантийное' },
            { Id: 11, Title: 'Новое' }
        ],
        []
    );

    const [selectedAuthors, setSelectedAuthors] = React.useState<User[]>([]);
    const [authors, setAuthors] = React.useState<User[]>([]);

    const [selectedWithComments, setSelectedWithComments] = React.useState(false);
    const [selectedWithImages, setSelectedWithImages] = React.useState(false);
    const [selectedOnlyLastInstance, setSelectedOnlyLastInstance] = React.useState(true);
    const [selectedOnlyCritical, setSelectedOnlyCritical] = React.useState(false);

    const [isGenerating, setIsGenerating] = React.useState(false);
    const pollingRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const startPolling = React.useCallback(() => {
        stopPolling();
        pollingRef.current = setInterval(() => {
            if (!projectDocId) return;
            services.paperSigningService.getPaperSigningStatus(projectDocId).then((res) => {
                const statusId = res.data.StatusId;
                if (statusId !== PaperSigningStatusVals.Pending && statusId !== PaperSigningStatusVals.InProgress) {
                    stopPolling();
                    setIsGenerating(false);
                    if (statusId === PaperSigningStatusVals.Completed) {
                        dispatch(alertSuccess({ detail: 'Вкладка бумажного подписания сформирована' }));
                        loadActions();
                        onPaperSigningGenerated?.();
                    } else {
                        dispatch(alertError({ detail: 'Ошибка формирования бумажного подписания' }));
                        loadActions();
                    }
                }
            });
        }, 5000);
    }, [projectDocId, loadActions, onPaperSigningGenerated]);

    React.useEffect(() => {
        if (!projectDocId) return;
        services.paperSigningService.getPaperSigningStatus(projectDocId).then((res) => {
            const statusId = res.data.StatusId;
            if (statusId === PaperSigningStatusVals.Pending || statusId === PaperSigningStatusVals.InProgress) {
                setIsGenerating(true);
                startPolling();
            }
        });
        return () => stopPolling();
    }, [projectDocId]);

    const generatePaperSigning = React.useCallback(() => {
        promiseWrapper(
            () => services.paperSigningService.generate(projectDocId),
            () => {
                setIsGenerating(true);
                startPolling();
            }
        );
    }, [projectDocId, startPolling]);

    React.useEffect(() => {
        setSelectedStatuses(statuses);
    }, [statuses]);

    React.useEffect(() => {
        setSelectedCyphers(cyphers);
    }, [cyphers]);

    React.useEffect(() => {
        if (!visible || cyphers.length < 1) {
            return;
        }
        promiseWrapper(
            () =>
                services.pDFNoteWorkflowInstanceService.fetchAuthorsForNotesRegistry({
                    WorkflowIds: [
                        WorkflowsVals.ProjectDocumentationApproval,
                        WorkflowsVals.WorkingDocumentationApproval,
                        WorkflowsVals.ExecutiveDocumentationApproval
                    ],
                    InstanceObjectIds: cyphers.map((x) => x.Id),
                    Sorts: [{ Field: 'Id', Desc: false }],
                    Take: 99999,
                    IsDeleted: false,
                    IncludeDeletedWorkflowInstances: false
                }),
            (users: User[]) => {
                setAuthors(users);
                setSelectedAuthors(users);
            }
        );
    }, [visible, cyphers]);

    const triggerBlobDownload = (data: Blob) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Реестр замечаний.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const accept = () => {
        promiseWrapper(
            () =>
                services.pDFNoteService.downloadNewNotesRegistry({
                    projectDocId,
                    filterOnlyCritical: selectedOnlyCritical,
                    filterWithImage: selectedWithImages,
                    filterWithComments: selectedWithComments,
                    filterOnlyLastInstance: selectedOnlyLastInstance,
                    filterProjectDocFolderIds:
                        selectedCyphers.length === cyphers.length ? [] : selectedCyphers.map((x) => x.Id),
                    filterPDFNoteStatuses:
                        selectedStatuses.length === statuses.length ? [] : selectedStatuses.map((x: any) => x.Title),
                    filterCreatedByIds:
                        selectedAuthors.length === authors.length ? [] : selectedAuthors.map((x: any) => x.Id)
                }),
            (data) => {
                triggerBlobDownload(data);
                setVisible(false);
            }
        );
    };

    const reject = () => {
        setVisible(false);
    };

    const footerContent = (
        <div className="pd-dialog-footer">
            <NewButton variant="secondary" icon="fa-solid fa-xmark" onClick={() => reject()}>
                Закрыть
            </NewButton>
            <NewButton variant="primary" icon="fa-solid fa-file-arrow-down" onClick={() => accept()}>
                Выгрузить
            </NewButton>
        </div>
    );

    const sendToApprove = React.useCallback(
        (workflowId: number, comment: string) => {
            promiseWrapper(
                () => services.workflowInstanceService.create(workflowId, projectDocId, comment),
                (res: WorkflowInstance) => {
                    dispatch(alertSuccess({ detail: `Отправлено на согласование` }));
                    if (res.Id && res.Id > 0) {
                        onProjectDocStatusUpdated();
                        loadActions();
                    }
                }
            );
            // eslint-disable-next-line
        },
        [projectDocId, onProjectDocStatusUpdated]
    );

    const getSendToApprove = React.useCallback(
        (workflowId: number) => {
            return (comment: string) => sendToApprove(workflowId, comment);
        },
        [sendToApprove]
    );

    const cancel = React.useCallback(
        (workflowId: number, comment: string) => {
            promiseWrapper(
                () => services.workflowInstanceService.cancel(workflowId, projectDocId, comment),
                (res: ValidationResult) => {
                    if (res.Errors.length > 0)
                        dispatch(alertError({ detail: `Ошибки: ${res.Errors.map((e) => e.Message).join('; ')}` }));
                    else {
                        dispatch(alertSuccess({ detail: `Отозвано с согласования` }));
                        onProjectDocStatusUpdated();
                        loadActions();
                    }
                }
            );
            // eslint-disable-next-line
        },
        [projectDocId, onProjectDocStatusUpdated]
    );

    const getCancel = React.useCallback(
        (workflowId: number) => {
            return (comment: string) => cancel(workflowId, comment);
        },
        [cancel]
    );

    return (
        <React.Fragment>
            <Dialog
                header="Реестр замечаний"
                footer={footerContent}
                visible={visible}
                className="pd-dialog"
                style={{ width: '50vw' }}
                onHide={() => {
                    if (!visible) return;
                    setVisible(false);
                }}
            >
                <div className="pd-dialog-body">
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Шифр тома</span>
                        <NewMultiSelect
                            value={selectedCyphers}
                            onChange={(e) => setSelectedCyphers(e.value)}
                            options={cyphers}
                            optionLabel="Label"
                            dataKey="Id"
                            filter
                            filterBy="Title,Cypher"
                            filterMatchMode="contains"
                            filterPlaceholder="Поиск по названию или шифру"
                            resetFilterOnHide
                            placeholder="Выберите шифры томов"
                            selectedItemsLabel={'Выбрано: {0}'}
                            className="w-100"
                        />
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Статус замечания</span>
                        <NewMultiSelect
                            value={selectedStatuses}
                            onChange={(e) => setSelectedStatuses(e.value)}
                            options={statuses}
                            optionLabel="Title"
                            placeholder="Все статусы замечаний"
                            selectedItemsLabel={'Выбрано: {0}'}
                            className="w-100"
                        />
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Автор замечания</span>
                        <NewMultiSelect
                            value={selectedAuthors}
                            onChange={(e) => setSelectedAuthors(e.value)}
                            options={authors}
                            optionLabel="Title"
                            placeholder="Все авторы замечаний"
                            selectedItemsLabel={'Выбрано: {0}'}
                            className="w-100"
                        />
                    </div>
                    <div className="pd-dialog-checks">
                        <NewCheckbox
                            checked={selectedWithComments}
                            onChange={(v) => setSelectedWithComments(v)}
                            label="Выгружать комментарии"
                        />
                        <NewCheckbox
                            checked={selectedWithImages}
                            onChange={(v) => setSelectedWithImages(v)}
                            label="Выгружать картинки"
                        />
                        <NewCheckbox
                            checked={selectedOnlyLastInstance}
                            onChange={(v) => setSelectedOnlyLastInstance(v)}
                            label="Только последняя итерация"
                        />
                        <NewCheckbox
                            checked={selectedOnlyCritical}
                            onChange={(v) => setSelectedOnlyCritical(v)}
                            label="Только критические"
                        />
                    </div>
                </div>
            </Dialog>

            <NewButton variant="secondary" block onClick={() => setVisible(true)}>
                <i className="fa-regular fa-file-excel" style={{ color: '#1c8a4d' }} />
                Скачать реестр замечаний
            </NewButton>
            {actions.length > 0 && (
                <React.Fragment>
                    {actions.map((action) => {
                        switch (action.Id) {
                            case ContractorProjectDocActions.SendToCompletionConfirmation:
                                return (
                                    <CommentButton
                                        label="Отправить на согласование"
                                        className="pd-file-action-btn pd-file-action-primary"
                                        icon="fa-solid fa-paper-plane"
                                        key={action.Id}
                                        commentIsRequired={false}
                                        disabled={isLoading}
                                        action={getSendToApprove(action.ObjectId)}
                                    />
                                );

                            case ContractorProjectDocActions.CancelCompletionConfirmation:
                                return (
                                    <CommentButton
                                        label="Отозвать с согласования"
                                        className="pd-file-action-btn pd-file-action-danger"
                                        icon="fa-solid fa-rotate-left"
                                        key={action.Id}
                                        commentIsRequired={false}
                                        disabled={isLoading}
                                        action={getCancel(action.ObjectId)}
                                    />
                                );
                            case ContractorProjectDocActions.GeneratePaperSigning:
                                return (
                                    <NewButton
                                        variant="secondary"
                                        icon={isGenerating ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-file-pdf'}
                                        key={action.Id}
                                        disabled={isLoading || isGenerating}
                                        onClick={generatePaperSigning}
                                    >
                                        {isGenerating
                                            ? 'Формирование комплектов для собственноручного подписания...'
                                            : 'Сформировать комплекты для собственноручного подписания'}
                                    </NewButton>
                                );
                            default:
                                return <React.Fragment key={action.Id}></React.Fragment>;
                        }
                    })}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};
